import { useState, useEffect } from 'react';
import { masterSupabase } from '../lib/supabase-master';
import { useAuth } from '../contexts/AuthContext';

const usePersonality = () => {
  const { user } = useAuth();
  const [personality, setPersonalityState] = useState('professional');
  const [personalityName, setPersonalityName] = useState('Professional Clara');
  const [availablePersonalities, setAvailablePersonalities] = useState([]);
  const [loading, setLoading] = useState(true);

  console.log('🤖 usePersonality Hook initialized');

  // Load available personalities from clara_personalities table
  const loadAvailablePersonalities = async () => {
    try {
      console.log('📥 Loading available personalities from Supabase...');
      
      const { data, error } = await masterSupabase
        .from('clara_personalities')
        .select('name, display_name, description')
        .eq('is_preset', true)
        .order('name');

      if (error) {
        console.error('❌ Error loading personalities:', error);
        return [];
      }

      console.log('✅ Available personalities loaded:', data);
      return data || [];
    } catch (error) {
      console.error('❌ Exception loading personalities:', error);
      return [];
    }
  };

  // Load user's selected personality from user_preferences
  const loadUserPersonality = async () => {
    if (!user) {
      console.log('👤 No user - using default personality');
      return 'professional';
    }

    try {
      console.log('📥 Loading user personality from Supabase for user:', user.id);
      
      const { data, error } = await masterSupabase
        .from('user_preferences')
        .select('preference_value')
        .eq('user_id', user.id)
        .eq('preference_key', 'clara_personality')
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('❌ Error loading user personality:', error);
        return 'professional';
      }

      const selectedPersonality = data?.preference_value || 'professional';
      console.log('✅ User personality loaded:', selectedPersonality);
      return selectedPersonality;
    } catch (error) {
      console.error('❌ Exception loading user personality:', error);
      return 'professional';
    }
  };

  // Save user's personality selection with CORRECT upsert syntax
  const setPersonality = async (personalityName) => {
    if (!user) {
      console.log('👤 No user - saving to localStorage as fallback');
      localStorage.setItem('clara_personality', personalityName);
      setPersonalityState(personalityName);
      updatePersonalityName(personalityName);
      return true;
    }

    try {
      console.log('💾 Saving user personality selection:', personalityName, 'for user:', user.id);
      
      // FIXED: Use correct upsert syntax with onConflict AND update fields
      const { error } = await masterSupabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          preference_key: 'clara_personality',
          preference_value: personalityName,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,preference_key',
          update: ['preference_value', 'updated_at']  // Explicitly specify which fields to update
        });

      if (error) {
        console.error('❌ Error saving user personality selection:', error);
        // Fallback to localStorage
        localStorage.setItem('clara_personality', personalityName);
        setPersonalityState(personalityName);
        updatePersonalityName(personalityName);
        return false;
      }

      console.log('✅ User personality selection saved successfully');
      setPersonalityState(personalityName);
      updatePersonalityName(personalityName);
      return true;
    } catch (error) {
      console.error('❌ Exception saving user personality selection:', error);
      // Fallback to localStorage
      localStorage.setItem('clara_personality', personalityName);
      setPersonalityState(personalityName);
      updatePersonalityName(personalityName);
      return false;
    }
  };

  // Update personality display name based on available personalities
  const updatePersonalityName = (personalityKey) => {
    const found = availablePersonalities.find(p => p.name === personalityKey);
    if (found) {
      setPersonalityName(found.display_name);
    } else {
      // Fallback mapping
      const fallbackNames = {
        'professional': 'Professional Clara',
        'friendly': 'Friendly Clara',
        'analytical': 'Analytical Clara'
      };
      setPersonalityName(fallbackNames[personalityKey] || 'Professional Clara');
    }
  };

  // Initialize hook
  useEffect(() => {
    const initializePersonality = async () => {
      setLoading(true);
      
      // Load available personalities first
      const personalities = await loadAvailablePersonalities();
      setAvailablePersonalities(personalities);
      
      // Load user's selected personality
      const userPersonality = await loadUserPersonality();
      setPersonalityState(userPersonality);
      
      // Update display name
      const found = personalities.find(p => p.name === userPersonality);
      if (found) {
        setPersonalityName(found.display_name);
      } else {
        const fallbackNames = {
          'professional': 'Professional Clara',
          'friendly': 'Friendly Clara', 
          'analytical': 'Analytical Clara'
        };
        setPersonalityName(fallbackNames[userPersonality] || 'Professional Clara');
      }
      
      setLoading(false);
      
      console.log('🎯 usePersonality returning:', {
        personality: userPersonality,
        personalityName: found ? found.display_name : fallbackNames[userPersonality] || 'Professional Clara',
        availablePersonalities: personalities.length,
        loading: false
      });
    };

    initializePersonality();
  }, [user]);

  // Reload function for external triggers
  const reload = async () => {
    const personalities = await loadAvailablePersonalities();
    setAvailablePersonalities(personalities);
    
    const userPersonality = await loadUserPersonality();
    setPersonalityState(userPersonality);
    updatePersonalityName(userPersonality);
  };

  return {
    personality,
    personalityName,
    availablePersonalities,
    setPersonality, // Now with FIXED upsert syntax!
    loading,
    reload
  };
};

export default usePersonality;

